// Ported 1:1 from mm-app (mm-common/mobile-keyboard/shared/keyboard-viewport.util.ts).

export function isIosDevice(navigatorRef: Navigator = navigator): boolean {
    const navAny: any = navigatorRef as any;
    const ua = navigatorRef.userAgent || '';
    const platform = navigatorRef.platform || '';
    const maxTouchPoints = typeof navAny?.maxTouchPoints === 'number' ? navAny.maxTouchPoints : 0;
    const vendor = (navAny.vendor || '').toString();
    const isIpadDesktopUa = /Macintosh/.test(ua) && /Mobile\//.test(ua);
    return (
        /iPad|iPhone|iPod|iPhone OS|iPadOS/i.test(ua) ||
        isIpadDesktopUa ||
        (platform === 'MacIntel' && maxTouchPoints > 1) ||
        (vendor.includes('Apple') && maxTouchPoints > 0 && /Safari\//.test(ua))
    );
}

export function isEditableElement(el: HTMLElement | null): boolean {
    if (!el) {
        return false;
    }
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function getActiveEditable(): HTMLElement | null {
    const active = document.activeElement as HTMLElement | null;
    return isEditableElement(active) ? active : null;
}

const TEXT_INPUT_TYPES = ['text', 'search', 'email', 'url', 'tel', 'password', 'number'];

// the tapped element's writable text field (input/textarea), if any — the targets whose focus
// summons the keyboard and therefore triggers WebKit's focus pan
export function findTextEntryTarget(target: EventTarget | null): HTMLInputElement | HTMLTextAreaElement | null {
    const el = target instanceof HTMLElement ? target.closest<HTMLInputElement | HTMLTextAreaElement>('input, textarea') : null;
    if (!el || el.disabled || el.readOnly) {
        return null;
    }
    if (el instanceof HTMLInputElement && TEXT_INPUT_TYPES.indexOf(el.type) === -1) {
        return null;
    }
    return el;
}

export function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
    let node = el ? el.parentElement : null;
    while (node) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

export function getFocusedScroller(focused: HTMLElement | null): HTMLElement {
    return (getScrollableAncestor(focused) || document.scrollingElement || document.documentElement) as HTMLElement;
}

// tracks addEventListener calls so they can all be removed in one stop()/clear().
export class ListenerGroup {

    private cleanups: Array<() => void> = [];

    add(target: EventTarget, type: string, handler: EventListener, options?: AddEventListenerOptions): void {
        target.addEventListener(type, handler, options);
        this.cleanups.push(() => target.removeEventListener(type, handler, options as any));
    }

    clear(): void {
        this.cleanups.forEach(cleanup => cleanup());
        this.cleanups = [];
    }

    get active(): boolean {
        return this.cleanups.length > 0;
    }
}

// keyboard/viewport signal set: visualViewport resize/scroll and focus changes. handler runs on each.
// touchstart too: WKWebView can keep reporting a keyboard-sized viewport after an overlay-dismissed
// keyboard (dropdown item select) without ever firing the final resize — it refreshes on the next
// touch, so recompute there to release the stale state.
export function onKeyboardViewportChange(group: ListenerGroup, handler: (event: Event) => void): void {
    if (window.visualViewport) {
        group.add(window.visualViewport, 'resize', handler);
        group.add(window.visualViewport, 'scroll', handler);
    }
    group.add(document, 'focusin', handler, { capture: true });
    group.add(document, 'focusout', handler, { capture: true });
    group.add(document, 'touchstart', handler, { capture: true, passive: true });
}
